import { toSafeString } from './toSafeString.js';
import { resolvePieveMcpActionsExecuteDisplayText, resolvePieveMcpDisplayAppLabel } from './resolvePieveMcpActionsExecuteDisplayText.js';

const toLimitedText = (value, maxLength = 48) => {
  const normalized = toSafeString(value);

  if (!normalized) {
    return null;
  }

  if (normalized.length <= maxLength) {
    return normalized;
  }

  return `${normalized.slice(0, maxLength)}...`;
};

const toReadableWords = (value) => {
  return toSafeString(value)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[._/-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const toLowerPhrase = (value) => {
  const words = toReadableWords(value);

  if (!words) {
    return null;
  }

  return words.toLowerCase();
};

const toDisplayQuery = (value) => {
  const limitedText = toLimitedText(value);

  if (!limitedText) {
    return null;
  }

  if (/[A-Z]/.test(limitedText)) {
    return limitedText;
  }

  return limitedText
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => {
      if (/^[A-Z0-9]{2,6}$/i.test(word) && word === word.toUpperCase()) {
        return word;
      }

      const normalizedWord = word.toLowerCase();
      return `${normalizedWord.charAt(0).toUpperCase()}${normalizedWord.slice(1)}`;
    })
    .join(' ');
};

const toLookupResolveResponseStatus = (args) => {
  const candidates = [args?.response, args?.output, args?.result];

  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== 'object') {
      continue;
    }

    const status = toSafeString(candidate?.data?.status || candidate?.status).toLowerCase();

    if (status) {
      return status;
    }
  }

  return null;
};

const toLookupLabel = ({ args, appLabel, lookupLabel }) => {
  const explicitLookupLabel = toSafeString(lookupLabel);

  if (explicitLookupLabel) {
    if (appLabel && !explicitLookupLabel.toLowerCase().startsWith(`${appLabel.toLowerCase()} `)) {
      return `${appLabel} ${explicitLookupLabel}`;
    }

    return explicitLookupLabel;
  }

  const argumentLabel = toLowerPhrase(args?.argumentLabel || args?.argumentName || args?.argumentKey);

  if (!argumentLabel) {
    return null;
  }

  if (!appLabel) {
    return argumentLabel;
  }

  return `${appLabel} ${argumentLabel}`;
};

export const resolvePieveMcpToolCallDisplayText = ({ toolName, args, appLabel: inputAppLabel = null, lookupLabel = null, displayQuery = null } = {}) => {
  const normalizedToolName = toSafeString(toolName).toLowerCase();
  const safeArgs = args && typeof args === 'object' && !Array.isArray(args) ? args : {};
  const appLabel =
    toSafeString(inputAppLabel) ||
    toSafeString(safeArgs?.appLabel) ||
    toSafeString(safeArgs?.appName) ||
    resolvePieveMcpDisplayAppLabel({
      value: safeArgs?.appRef || null
    });

  if (normalizedToolName === 'pieve_mcp_apps_search') {
    const effectiveDisplayQuery = toDisplayQuery(displayQuery || safeArgs?.query || safeArgs?.q);

    if (effectiveDisplayQuery) {
      return `Searching for apps like "${effectiveDisplayQuery}"`;
    }

    return 'Searching for apps';
  }

  if (normalizedToolName === 'pieve_mcp_apps_get') {
    if (!appLabel) {
      return null;
    }

    return `Checking what ${appLabel} can do`;
  }

  if (normalizedToolName === 'pieve_mcp_actions_search') {
    if (!appLabel) {
      return null;
    }

    const searchKeyword = toLimitedText(safeArgs?.q || safeArgs?.query);

    if (searchKeyword) {
      return `Finding the right step in ${appLabel} for "${searchKeyword}"`;
    }

    return `Finding the right step in ${appLabel}`;
  }

  if (normalizedToolName === 'pieve_mcp_connections_list' || normalizedToolName === 'pieve_mcp_connections_search') {
    if (!appLabel) {
      return null;
    }

    return `Checking your ${appLabel} connection`;
  }

  if (normalizedToolName === 'pieve_mcp_connections_resolve') {
    if (!appLabel) {
      return null;
    }

    return `Choosing the ${appLabel} account`;
  }

  if (normalizedToolName === 'pieve_mcp_lookup_resolve') {
    const resolvedLookupLabel = toLookupLabel({
      args: safeArgs,
      appLabel,
      lookupLabel
    });

    /* A lookup that returned status not_found must not render as "Found the ..." after the
       past-tense swap (2026-08-11). The response only rides in args once the call completed,
       so its presence is the completion signal; "Failed ..." has no entry in the tense maps
       and passes through the swap untouched. */
    if (toLookupResolveResponseStatus(safeArgs) === 'not_found') {
      if (resolvedLookupLabel) {
        return `Could not find the ${resolvedLookupLabel}`;
      }

      if (!appLabel) {
        return null;
      }

      return `Could not find the right ${appLabel} option`;
    }

    if (resolvedLookupLabel) {
      return `Finding the ${resolvedLookupLabel}`;
    }

    if (!appLabel) {
      return null;
    }

    return `Finding the right ${appLabel} option`;
  }

  if (normalizedToolName === 'pieve_mcp_action_describe' || normalizedToolName === 'pieve_mcp_action_schema') {
    if (!appLabel) {
      return null;
    }

    const actionLabel = toSafeString(safeArgs?.actionLabel);

    if (actionLabel) {
      return `Checking what ${appLabel} needs for ${actionLabel}`;
    }

    return `Checking what ${appLabel} needs`;
  }

  if (normalizedToolName === 'pieve_mcp_action_execute' || normalizedToolName === 'pieve_mcp_actions_execute') {
    return resolvePieveMcpActionsExecuteDisplayText({
      args: {
        ...safeArgs,
        ...(appLabel
          ? {
              appLabel
            }
          : {})
      }
    });
  }

  if (normalizedToolName === 'pieve_mcp_result_query') {
    return 'Refining the results';
  }

  return null;
};
